/**
 * L'adresse de la page que le visiteur a SOUS LES YEUX.
 *
 * D'ordinaire, c'est l'adresse courante. Mais une page de site (un formulaire,
 * le plus souvent) peut être intégrée en iframe sur le site d'un client
 * (cf. `ORBIT_FRAME_ANCESTORS`) : `location.href` y donne alors l'adresse de la
 * FRAME — toujours la même —, et chaque soumission dirait venir du formulaire
 * au lieu de la page du client où il a été rempli. Pour savoir d'où vient un
 * contact, c'est la page APPELANTE qu'il faut joindre.
 *
 * Une frame d'une autre origine n'a pas le droit de lire l'adresse de son
 * parent ; on prend donc la meilleure piste disponible, de la plus sûre à la
 * plus pauvre :
 *
 *  1. `window.top.location.href` — seulement si le parent est de la même
 *     origine (sinon le navigateur lève une erreur de sécurité) ;
 *  2. `?parentUrl=…` dans l'adresse de la frame — l'intégrateur qui veut
 *     l'adresse COMPLÈTE quel que soit le navigateur la passe lui-même
 *     (`iframe.src = '…?parentUrl=' + encodeURIComponent(location.href)`) ;
 *  3. `document.referrer` — l'adresse du parent au chargement de la frame,
 *     s'il est d'une AUTRE origine (une référence de même origine n'est que la
 *     page précédente de la frame, après une navigation interne). Par défaut
 *     (`strict-origin-when-cross-origin`) le navigateur la réduit à l'origine ;
 *     elle est complète si l'iframe porte `referrerpolicy="unsafe-url"` (ou
 *     `no-referrer-when-downgrade`) ;
 *  4. la dernière adresse de parent retenue pour cet onglet — la frame a pu
 *     naviguer (étape 2 d'un formulaire), le `referrer` est alors perdu ;
 *  5. `location.ancestorOrigins` — l'origine seule (Chromium, Safari) ;
 *  6. à défaut, l'adresse de la frame elle-même.
 *
 * Hors iframe, rien ne change : c'est `location.href`.
 */

const STORAGE_KEY = 'parentPageUrl'

/** Paramètre d'URL par lequel l'intégrateur transmet l'adresse du parent. */
export const PARENT_URL_PARAM = 'parentUrl'

/** Une adresse http(s) absolue, sinon `null` (jamais un `javascript:`). */
const httpUrl = (value) => {
  if (typeof value !== 'string' || value === '') return null
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null
  } catch {
    return null
  }
}

const isFramed = (win) => {
  try {
    return win.self !== win.top
  } catch {
    // Accès à `top` refusé : on est forcément dans une frame.
    return true
  }
}

const readStored = (win) => {
  try {
    return win.sessionStorage?.getItem(STORAGE_KEY) ?? null
  } catch {
    return null
  }
}

const remember = (win, url) => {
  try {
    win.sessionStorage?.setItem(STORAGE_KEY, url)
  } catch {
    // Stockage bloqué (navigation privée, cookies tiers refusés) : sans gravité.
  }
  return url
}

/**
 * @param {Window} [win]
 *   Injecté pour les tests ; `window` par défaut.
 *
 * @return {string}
 *   L'adresse de la page vue par le visiteur, `''` hors navigateur (SSG).
 */
export const visitorPageUrl = (win = typeof window === 'undefined' ? undefined : window) => {
  if (!win) return ''
  const own = win.location?.href ?? ''
  if (!isFramed(win)) return own

  // 1. Parent de même origine : lisible directement.
  try {
    const top = httpUrl(win.top.location.href)
    if (top) return remember(win, top)
  } catch {
    // Origine différente : on passe aux pistes indirectes.
  }

  // 2. Adresse transmise explicitement par l'intégrateur.
  const fromParam = httpUrl(new URLSearchParams(win.location?.search ?? '').get(PARENT_URL_PARAM))
  if (fromParam) return remember(win, fromParam)

  // 3. Le referrer, s'il vient bien d'ailleurs que de la frame.
  const referrer = httpUrl(win.document?.referrer)
  if (referrer && new URL(referrer).origin !== win.location?.origin) return remember(win, referrer)

  // 4. Retenue au premier chargement de la frame dans cet onglet — à condition
  //    d'être chez le MÊME parent : l'onglet a pu passer d'un client à l'autre
  //    sans que le nouveau chargement porte de referrer.
  const ancestor = httpUrl(win.location?.ancestorOrigins?.[0])
  const stored = httpUrl(readStored(win))
  if (stored && (!ancestor || new URL(stored).origin === new URL(ancestor).origin)) return stored

  // 5. L'origine du parent, faute de mieux.
  if (ancestor) return ancestor

  return own
}
